import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import { renderDivisionPrice } from '../../utils/events';
import { utrTypeIds } from '../../utils/eventConstants';
import { getApiAxiosInstance } from '../../utils/network';
import DivisionCard from './DivisionCard';
import * as checkoutEventRegistrationApi from '../../api/checkoutEventRegistrationApi';
import * as eventProfileActions from '../../actions/event-profile-actions';

// supports V2 typeIds
const SwitchDivisions = ({
    event,
    user,
    clubVerificationFee,
    selectedDivisionIds,
    canceledDivisionIds,
    switchTotalRefund,
    handleCloseClick,
    handleToPayment,
    switchApiResponse,
    setSwitchApiResponse,
    registrationTotal,
    switchPlayer2,
    registrations,
}) => {
    const [eligibleDivisions, setEligibleDivisions] = useState([]);
    const [submitting, setSubmitting] = useState();
    const [error, setError] = useState();
    const dispatch = useDispatch();

    const isSanctioned = event.utrType?.value === 'sanctioned' || event.utrTypeId === utrTypeIds.SANCTIONED;
    const apiInstance = getApiAxiosInstance();
    const selectedDivisions = event.eventDivisions.filter(d => selectedDivisionIds.includes(d.id));
    const selectedDivisionFee = event.eventDivisions.find(d => selectedDivisionIds.includes(d.id))?.entryFee;
    const canceledDivisionFee = event.eventDivisions.find(d => canceledDivisionIds.includes(d.id))?.entryFee;

    useEffect(() => {
        const divisionIds = event.currentMember.registeredDivisionIds;
        const allRegisteredDivisions = event.eventDivisions.filter(div => divisionIds.includes(div.id));
        // user specific division props from the /divisions/registrations endpoint
        const registrationsDivs = registrations?.data?.registeredDivisions || [];

        const {
            eligible,
        } = allRegisteredDivisions.reduce((acc, curr) => {
            const divObjFromRegistrationsApi = registrationsDivs.find(div => div.divisionId === curr.id);
            const { selfWithdrawAllowed, autoRefundEnabled, total, availableDivisionsForSwitching } = divObjFromRegistrationsApi || {};
            if (availableDivisionsForSwitching?.length) {
                acc.eligible.push({
                    ...curr,
                    selfWithdrawAllowed,
                    autoRefundEnabled,
                    isRefundable: selfWithdrawAllowed && autoRefundEnabled && total > 0,
                });
            }
            return acc;
        }, {
            eligible: [],
        });

        setEligibleDivisions(eligible);
    }, []);

    const renderPrice = (division) => {
        return renderDivisionPrice(
            division,
            event.allowOffPlatformPayments,
            isSanctioned,
            event.workflowVersion,
            user,
            clubVerificationFee,
        );
    };

    const handleNextClick = async () => {
        // no payment needed
        if (selectedDivisionFee === canceledDivisionFee || registrationTotal === 0) {
            const payload = {
                oldDivisionIds: canceledDivisionIds,
            };
            payload.newDivisions = selectedDivisions.map((div) => {
                const division = {
                    divisionId: div.id,
                    eventId: event.id,
                };
                if (switchPlayer2) {
                    if (switchPlayer2.needsPartner) {
                        division.needsPartner = true;
                    } else if (switchPlayer2.id) {
                        if (switchPlayer2.profileId > 0) {
                            division.partnerIds = [switchPlayer2.id];
                        } else {
                            division.playerInvites = [
                                {
                                    id: switchPlayer2.id,
                                    name: switchPlayer2.displayName,
                                    email: switchPlayer2.email,
                                },
                            ];
                        }
                    } else {
                        division.playerInvites = [
                            {
                                name: switchPlayer2.name,
                                email: switchPlayer2.email,
                            },
                        ];
                    }
                    if (switchPlayer2.partnerAnswers) {
                        division.partnerAnswers = switchPlayer2.partnerAnswers;
                    }
                }
                return division;
            });

            try {
                setSubmitting(true);
                const res = await checkoutEventRegistrationApi.legacySwitchDivisions(apiInstance, event.id, payload);
                setSubmitting(false);
                if (res.status === 200) {
                    setSwitchApiResponse(res.data);
                }
            } catch (e) {
                setError(e);
                setSubmitting(false);
            }
        } else {
            // payment needed
            handleToPayment();
        }
    };

    const handleFinishClick = () => {
        dispatch(eventProfileActions.getEvent(event.id));
        handleCloseClick();
    };

    const eventDivisions = event?.eventDivisions || [];

    const canceledDivisionNames = canceledDivisionIds.map(divId => eventDivisions.find(div => div.id === divId)?.name);
    const selectedDivisionNames = selectedDivisions.map(div => div.name);

    const getPaymentConfirmationText = (switchApiResponse) => {
        let confirmationText;
        if (selectedDivisionFee === canceledDivisionFee) {
            confirmationText = <p>There was no charge and no refund.</p>;
        } else if (switchApiResponse.totalRefunded && registrationTotal === 0) {
            confirmationText = <p>You have been issued a <span className="text-medium">refund of {event.currency.symbol}{switchApiResponse.totalRefunded.totalRefundAmount?.toFixed(2)}</span>.</p>;
        } else if ((!switchApiResponse.totalRefunded || switchApiResponse.totalRefunded.totalRefundAmount === 0) && registrationTotal !== 0) {
            confirmationText = <p>You have been charged <span className="text-medium">{event.currency.symbol}{registrationTotal?.toFixed(2)}</span>.</p>;
        } else {
            confirmationText = <p>You have been issued a <span className="text-medium">refund of {event.currency.symbol}{switchApiResponse.totalRefunded?.totalRefundAmount?.toFixed(2)}</span>, and <span className="text-medium">charged {event.currency.symbol}{registrationTotal?.toFixed(2)}</span>.</p>;
        }
        return (
            <div className="mb24 text-dark">
                {confirmationText}
            </div>
        );
    };

    if (switchApiResponse) {
        // confirmation screen
        return (
            <>
                <div className="mb16 text-dark">
                    <div className="text-highlight2 mb24">Confirmation</div>
                    <div className="mb24">
                        <p>You have successfully changed your division registration from <span className="text-medium">{canceledDivisionNames.join(', ')}</span> to <span className="text-medium">{selectedDivisionNames.join(', ')}</span>.</p>
                    </div>
                    {getPaymentConfirmationText(switchApiResponse)}
                    <div className="mb40">
                        <p>Thank you for playing.</p>
                    </div>
                </div>
                <button
                    onClick={handleFinishClick}
                    type="button"
                    className={`btn btn-primary btn-xl btn-block text-uppercase mt-3`}
                    data-testid="event-reg.switch-divisions.ok-btn"
                >
                    OK
                </button>
            </>
        );
    }

    const getConfirmationText = () => {
        if (selectedDivisionFee === canceledDivisionFee) {
            return <p className="text-dark">There will be no charge and no refund.</p>;
        }
        if (registrationTotal === 0) {
            return <p className="text-dark">You will be refunded {event.currency.symbol}{switchTotalRefund?.toFixed(2)}.</p>;
        }
        if (switchTotalRefund === 0) {
            return <p className="text-dark">You will be charged {event.currency.symbol}{registrationTotal?.toFixed(2)} (includes service fees).</p>;
        }
        return <p className="text-dark">You will be refunded {event.currency.symbol}{switchTotalRefund?.toFixed(2)}, and charged {event.currency.symbol}{registrationTotal?.toFixed(2)} (includes service fees).</p>;
    };

    return (
        <>
            <div className="mb16">
                <div className="text-highlight2 text-dark">Change division registrations from</div>
            </div>
            {eligibleDivisions
                .filter(div => canceledDivisionIds?.includes(div.id))
                .map((division) => {
                return (
                    <DivisionCard
                        key={division.id}
                        division={division}
                        divisionPrice={() => renderPrice(division)}
                        description={division.description}
                    />
                );
            })}
            <div className="mb16">
                <div className="text-highlight2 text-dark">To</div>
            </div>
            {eventDivisions
                .filter(div => selectedDivisionIds?.includes(div.id))
                .map((division) => {
                return (
                    <DivisionCard
                        key={division.id}
                        division={division}
                        divisionPrice={() => renderPrice(division)}
                        description={division.description}
                    />
                );
            })}

            {getConfirmationText()}

            <button
                onClick={handleNextClick}
                disabled={submitting === true}
                type="button"
                className={`btn btn-primary btn-xl btn-block text-uppercase mt-3 ${submitting ? 'text-dark' : ''}`}
                data-testid="event-reg.switch-divisions.next-btn"
            >
                {submitting && (<i className="fas fa-circle-notch fa-spin" />)}{submitting ? 'Processing' : 'Next'}
            </button>

            {error && (
                <p className="text-small text-error text-center mt16">There was an error processing your request.</p>
            )}
        </>
    );
};

SwitchDivisions.propTypes = {
    user: PropTypes.objectOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    clubVerificationFee: PropTypes.number,
    switchTotalRefund: PropTypes.number,
    handleToPayment: PropTypes.func,
    handleCloseClick: PropTypes.func,
    switchApiResponse: PropTypes.objectOf(any),
    setSwitchApiResponse: PropTypes.func.isRequired,
    registrationTotal: PropTypes.number,
    switchPlayer2: PropTypes.objectOf(any),
};

SwitchDivisions.defaultProps = {
    clubVerificationFee: 0,
    switchTotalRefund: 0,
    handleCloseClick: () => { },
    handleToPayment: () => { },
    switchApiResponse: null,
    registrationTotal: 0,
    switchPlayer2: null,
};

export default SwitchDivisions;
